"use client"
import { useState, useEffect } from 'react'
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { ArrowLeft, Loader2, Download, Search, RefreshCw, Plus, CalendarRange } from "lucide-react"
import { columns } from "./columns"
import { DataTable } from "@/components/data-table"
import TransactionsService from '@/api/transactions.service'
import * as XLSX from 'xlsx'
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import { Badge } from "@/components/ui/badge"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { format } from "date-fns"

// Define the necessary type directly in this file to avoid import issues
interface AccountTransaction {
  id?: number
  publicId: string
  businessId: number
  chartOfAccountId: string
  transactionType: 'DEBIT' | 'CREDIT'
  amount: number
  currency: string
  description?: string
  reference?: string
  referenceId?: string
  referenceType?: string
  transactionDate: string
  chartOfAccount?: any
  userName?: string
  [key: string]: any // Allow other properties
}

async function getData(setCanLoadMore: any, skip: number, businessId: number): Promise<AccountTransaction[]> {
  try {
    const response = await TransactionsService.getAllTransactions(businessId, skip);
    console.log("getAllTransactions response.data", response.data);
    if (response.data.result === 'invalid') {
      window.location.href = '/logout';
      return [];
    }
    // Notify if exactly 50 transactions are returned
    if (response.data.accountTransactions.length === 50) {
      setCanLoadMore(true);
    }
    return response.data.accountTransactions;
  } catch (error) {
    console.error("Error fetching transactions:", error);
    return [];
  }
}

export default function Transactions() {
  const [data, setData] = useState<AccountTransaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [skip, setSkip] = useState(0);
  const [canLoadMore, setCanLoadMore] = useState(false);
  
  // Filter states
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [accountId, setAccountId] = useState<string | null>(null);
  const [referenceType, setReferenceType] = useState<string | null>(null);
  const [transactionType, setTransactionType] = useState<string | null>(null);

  // For statistics
  const [totalDebit, setTotalDebit] = useState(0);
  const [totalCredit, setTotalCredit] = useState(0);
  const [transactionCount, setTransactionCount] = useState(0);

  const [selectedBusiness] = useState(() => {
    const storedBusiness = typeof window !== 'undefined' ? window.localStorage.getItem('selectedBusiness') : null;
    return storedBusiness ? JSON.parse(storedBusiness) : null;
  });

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      const businessId = selectedBusiness?.id;
      if (!businessId) return;
      
      let result: AccountTransaction[] = [];
      
      // Check if date range filter is applied
      if (startDate && endDate) {
        const startDateObj = new Date(startDate);
        const endDateObj = new Date(endDate);
        
        const response = await TransactionsService.getTransactionsByDateRange(
          businessId,
          startDateObj.getFullYear(),
          startDateObj.getMonth() + 1,
          startDateObj.getDate(),
          endDateObj.getFullYear(),
          endDateObj.getMonth() + 1,
          endDateObj.getDate(),
          accountId ? accountId : undefined
        );
        
        if (response.data.result === 'success') {
          result = response.data.transactions || [];
        }
      } else {
        result = await getData(setCanLoadMore, skip, businessId);
      }
      
      // Apply other filters
      if (referenceType) {
        result = result.filter(tx => tx.referenceType === referenceType);
      }
      
      if (transactionType) {
        result = result.filter(tx => tx.transactionType === transactionType);
      }
      
      setData(result);
      setIsLoading(false);
      
      // Calculate statistics
      let debitTotal = 0;
      let creditTotal = 0;
      
      result.forEach(tx => {
        if (tx.transactionType === 'DEBIT') {
          debitTotal += tx.amount;
        } else {
          creditTotal += tx.amount;
        }
      });
      
      setTotalDebit(debitTotal);
      setTotalCredit(creditTotal);
      setTransactionCount(result.length);
    };
    
    fetchData();
  }, [skip, startDate, endDate, accountId, referenceType, transactionType, selectedBusiness?.id]);

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Transactions");
    const currentDateTime = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    XLSX.writeFile(workbook, `Transactions_${currentDateTime}.xlsx`);
  };

  const handleFilterReset = () => {
    setStartDate(null);
    setEndDate(null);
    setAccountId(null);
    setReferenceType(null);
    setTransactionType(null);
    setSkip(0);
  };

  const handleDateRangeSearch = () => {
    if (startDate && endDate) {
      setSkip(0); // Reset pagination when applying filters
    }
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            onClick={() => window.history.back()} 
            variant="ghost" 
            size="icon" 
            className="rounded-full h-8 w-8"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-semibold">Account Transactions</h1>
          <Badge variant="outline" className="ml-2 bg-blue-50 text-blue-700 hover:bg-blue-50">
            {transactionCount} transactions
          </Badge>
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            onClick={exportToExcel} 
            variant="outline" 
            className="flex items-center gap-2 text-sm font-medium"
          >
            <Download className="h-4 w-4" /> Export
          </Button>
          
          <Button 
            onClick={() => window.location.href = "/transactions/create"} 
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Plus className="h-4 w-4" /> Record Transaction
          </Button>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-slate-50">
          <CardContent className="pt-6">
            <div className="text-sm font-medium text-muted-foreground mb-1">Debit Transactions</div>
            <div className="text-2xl font-bold text-amber-600">
              {(totalDebit).toLocaleString(undefined, { 
                style: 'currency', 
                currency: 'USD', 
                minimumFractionDigits: 2 
              })}
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-50">
          <CardContent className="pt-6">
            <div className="text-sm font-medium text-muted-foreground mb-1">Credit Transactions</div>
            <div className="text-2xl font-bold text-blue-600">
              {(totalCredit).toLocaleString(undefined, { 
                style: 'currency', 
                currency: 'USD', 
                minimumFractionDigits: 2 
              })}
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-50">
          <CardContent className="pt-6">
            <div className="text-sm font-medium text-muted-foreground mb-1">Net Movement</div>
            <div className={`text-2xl font-bold ${(totalCredit - totalDebit) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {((totalCredit - totalDebit)).toLocaleString(undefined, { 
                style: 'currency', 
                currency: 'USD', 
                minimumFractionDigits: 2 
              })}
            </div>
          </CardContent>
        </Card>
      </div>
      
      <Card className="shadow-sm border-0">
        <CardHeader className="pb-4">
          <div className="flex justify-between items-center">
            <CardTitle className="text-lg">Transaction Filters</CardTitle>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={handleFilterReset}
              className="text-muted-foreground"
            >
              Reset Filters
            </Button>
          </div>
        </CardHeader>
        
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="space-y-1">
              <label className="text-sm font-medium text-slate-700" htmlFor="startDate">From Date</label>
              <Input 
                id="startDate" 
                type="date" 
                value={startDate || ''} 
                onChange={(e) => setStartDate(e.target.value)}
                className="h-10 border-slate-200 focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            
            <div className="space-y-1">
              <label className="text-sm font-medium text-slate-700" htmlFor="endDate">To Date</label>
              <Input 
                id="endDate" 
                type="date" 
                value={endDate || ''} 
                onChange={(e) => setEndDate(e.target.value)}
                className="h-10 border-slate-200 focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            
            <div className="space-y-1">
              <label className="text-sm font-medium text-slate-700">Reference Type</label>
              <Select value={referenceType || ''} onValueChange={setReferenceType}>
                <SelectTrigger>
                  <SelectValue placeholder="All references" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All references</SelectItem>
                  <SelectItem value="invoice">Invoice</SelectItem>
                  <SelectItem value="receipt">Receipt</SelectItem>
                  <SelectItem value="bill">Bill</SelectItem>
                  <SelectItem value="quotation">Quotation</SelectItem>
                  <SelectItem value="manual">Manual Entry</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-1">
              <label className="text-sm font-medium text-slate-700">Transaction Type</label>
              <Select value={transactionType || ''} onValueChange={setTransactionType}>
                <SelectTrigger>
                  <SelectValue placeholder="All types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  <SelectItem value="DEBIT">Debit</SelectItem>
                  <SelectItem value="CREDIT">Credit</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-end">
              <Button 
                onClick={handleDateRangeSearch} 
                className="w-full bg-blue-600 hover:bg-blue-700 text-white h-10"
                disabled={isLoading || (!startDate && !endDate)}
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <CalendarRange className="h-4 w-4 mr-2" />
                )}
                Apply Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card className="shadow-sm border-0">
        <CardHeader className="pb-2">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="relative w-full md:w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search transactions..."
                className="pl-9 h-10 w-full md:w-[300px] bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 focus-visible:ring-blue-500"
              />
            </div>
            
            <div className="flex items-center text-sm text-muted-foreground">
              {startDate && endDate && (
                <div className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-medium flex items-center">
                  <CalendarRange className="h-3 w-3 mr-1" />
                  {format(new Date(startDate), "MMM d, yyyy")} - {format(new Date(endDate), "MMM d, yyyy")}
                </div>
              )}
            </div>
          </div>
        </CardHeader>
        
        <Separator className="mb-0" />
        
        <CardContent className="p-0">
          <DataTable columns={columns} data={data} section="transactions" isLoading={isLoading} />
          
          {canLoadMore && !startDate && !endDate && (
            <div className="flex flex-col items-center py-6">
              <Button 
                onClick={() => setSkip(skip + 50)} 
                variant="outline" 
                className="border-blue-200 text-blue-700 hover:bg-blue-50 hover:text-blue-800 transition-all"
                disabled={isLoading}
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Load More
              </Button>
              <p className="text-sm text-slate-500 mt-2">
                Showing {skip + 1} to {skip + Math.min(data.length, 50)} of {skip + data.length}+ transactions
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
} 